import { InfoCircleFilled } from '@ant-design/icons'
import { Popover, Tag, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'

import {
  InventoryItemResponse,
  InventoryStatus,
  Permissions
} from 'src/api/requests'
import { DangerIcon, WarningIcon } from 'src/assets'
import { Text } from 'src/components'
import { useUser } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'

import { ChangeStatusPopover, QuantityPopover } from '../components'
import { getStockValidStatus } from '../utils'

export const useColumns = () => {
  const { t, currentLang } = useLocales()

  const { hasPermission } = useUser()

  const palette = useTheme()

  const columns: ColumnsType<InventoryItemResponse> = [
    {
      key: 'commercialName',
      width: 200,
      title: t('recipes.commercial_name'),
      render: ({ product, doses }) => (
        <Text weight={400} color={palette.blue6}>
          {product?.name} {doses}
        </Text>
      ),
      fixed: 'left'
    },
    {
      width: 300,
      key: 'legalEntity',
      title: t('stock_filters.legalEntityIds'),
      render: (_, { legalEntity }) => (
        <Text weight={400} color={palette.blue6}>
          {legalEntity?.name}
        </Text>
      ),
      fixed: 'left'
    },
    {
      key: 'id',
      title: t('stock_applications.series'),
      dataIndex: 'barCode',
      fixed: 'left'
    },
    {
      key: 'internationalName',
      title: t('common.commercial_name'),
      render: ({ internationalName }) => (
        <Text weight={400} color={palette.blue6}>
          {internationalName?.name}
        </Text>
      )
    },
    {
      key: 'producer',
      title: t('common.producer'),
      render: ({ producer }) => (
        <Text color={palette.cyan7}>{producer?.name}</Text>
      )
    },
    {
      key: 'measurementUnit',
      width: 150,
      title: t('medicine_register.consumables.measurement_unit'),
      align: 'center',
      render: ({ measurementUnit }) => (
        <Text weight={400} color={palette.blue}>
          {measurementUnit?.name
            ? measurementUnit?.name?.[currentLang?.value]
            : '-'}
        </Text>
      )
    },
    {
      key: 'productGroup',
      width: 150,
      title: t('medicine_register.pharmaceutical_form.title'),
      render: ({ productForm }) => (
        <Text weight={400} color={palette.green7}>
          {productForm?.name}
        </Text>
      )
    },
    {
      key: 'productForm',
      width: 150,
      title: t('common.group'),
      align: 'center',
      render: ({ productGroup }) => (
        <Text weight={400} color={palette.blue}>
          {productGroup?.name ? productGroup?.name?.[currentLang?.value] : '-'}
        </Text>
      )
    },
    {
      key: 'production_date',
      align: 'center',
      title: t('common.production_date'),
      render: ({ productionDate }) =>
        dayjs(productionDate).format(dayMonthYearBackSlash)
    },
    {
      key: 'expiration_date',
      align: 'center',
      title: t('common.expiration_date'),
      render: ({ expirationDate }) =>
        dayjs(expirationDate).format(dayMonthYearBackSlash)
    },
    {
      key: 'in_date',
      align: 'center',
      title: t('stock_administration.entry_date'),
      render: ({ inDate }) => dayjs(inDate).format(dayMonthYearBackSlash)
    },
    {
      key: 'inQuantity',
      width: 150,
      title: t('common.quantity'),
      render: ({ currentQuantity, internationalName }) => (
        <Popover
          placement="right"
          color={palette.colorBgSpotlight}
          content={
            <QuantityPopover
              quantity={currentQuantity}
              internationalNameId={internationalName?.id}
            />
          }
        >
          <span className="pointer">
            {currentQuantity}{' '}
            <InfoCircleFilled
              style={{
                color: palette.colorIcon
              }}
            />
          </span>
        </Popover>
      )
    },

    {
      key: 'tvaRate',
      title: t('common.tva'),
      align: 'center',
      render: ({ tvaRate }) => (
        <Text weight={400}>
          {tvaRate?.name ? tvaRate?.name?.[currentLang?.value] : '-'}
        </Text>
      )
    },
    {
      key: 'costPerUnitWithTva',
      width: 150,
      title: t('common.price_unit'),
      align: 'center',
      render: ({ finalUnitPrice }) => <Text weight={400}>{finalUnitPrice}</Text>
    },
    {
      key: 'info',
      title: t('common.info'),
      render: ({ inQuantity, expirationDate }) => {
        const isExpired = dayjs().isAfter(
          dayjs(expirationDate, dayMonthYearBackSlash)
        )

        if (isExpired || !inQuantity)
          return (
            <Tooltip
              title={isExpired ? t('stock.expired') : t('stock.out_of_stock')}
              placement="left"
            >
              {/* children need to be native html tags */}
              <div>{isExpired ? <DangerIcon /> : <WarningIcon />}</div>
            </Tooltip>
          )
      }
    },
    {
      key: 'status',
      fixed: 'right',
      align: 'center',
      width: 150,
      title: t('form.status'),
      render: ({ id, status }) =>
        status !== InventoryStatus.EXPIRED &&
        hasPermission(Permissions.STOCKS_INVENTORY_UPDATE_SETTINGS) ? (
          <ChangeStatusPopover id={id} status={status} />
        ) : (
          <Tag color={getStockValidStatus(t, status).color}>
            {getStockValidStatus(t, status).text}
          </Tag>
        )
    }
  ]

  return { columns }
}
